import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { Phone, ChevronDown } from "lucide-react";
import { SITE, HERO_STATS } from "@/data/content";
import { MaskedLine, EASE } from "./Reveal";

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 200]);
  const fade = useTransform(scrollY, [0, 480], [1, 0]);

  const mx = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const bgX = useTransform(sx, (v) => v * 24);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMouseMove} className="relative min-h-[92svh] lg:min-h-screen flex flex-col overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: yBg, x: bgX }}>
        <motion.img
          src={SITE.heroImage}
          srcSet={`${SITE.heroImage.replace("w=1920", "w=800")} 800w, ${SITE.heroImage.replace("w=1920", "w=1400")} 1400w, ${SITE.heroImage} 1920w`}
          sizes="100vw"
          alt="Aerial view of highways and infrastructure at Dholera Smart City, Gujarat — premium property investment destination"
          className="w-full h-full object-cover img-treatment"
          initial={{ scale: 1.18 }}
          animate={{ scale: 1.02 }}
          transition={{ duration: 2.6, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/70 to-navy/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />

      <motion.div className="relative z-10 container-luxe flex-1 flex flex-col justify-center pt-32 pb-16" style={{ opacity: fade }}>
        <motion.p className="eyebrow !text-gold mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}>
          Dholera Smart City · Gujarat · India
        </motion.p>

        <h1 data-testid="hero-title" className="font-serif font-semibold text-white text-[clamp(2.6rem,6.5vw,5.4rem)] leading-[1.04] tracking-tight max-w-5xl">
          <MaskedLine delay={0.25}>Invest in the Future</MaskedLine>
          <MaskedLine delay={0.4}>
            of <em className="italic text-gold">Dholera Smart City</em>
          </MaskedLine>
        </h1>

        <motion.p
          className="mt-6 text-white/80 text-base sm:text-lg max-w-xl leading-relaxed"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.9, ease: EASE }}
        >
          Premium Real Estate Opportunities in Gujarat's Emerging Smart City.
        </motion.p>

        <motion.div
          className="mt-9 flex flex-wrap items-center gap-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: EASE }}
        >
          <a href="#properties" data-testid="hero-cta-explore" className="btn-gold">
            Explore Properties
          </a>
          <a href="#contact" data-testid="hero-cta-enquire" className="btn-ghost-light">
            Enquire Now
          </a>
          <a href={SITE.phoneHref} data-testid="hero-cta-call" className="btn-ghost-light !border-gold/60 !text-gold hover:!bg-gold hover:!text-navy">
            <Phone className="w-4 h-4" /> Call Now
          </a>
        </motion.div>

        <motion.p
          className="mt-8 font-serif italic text-gold-light/90 text-lg flex items-center gap-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.15, duration: 1 }}
        >
          <span className="w-10 h-px bg-gold inline-block" />
          Your Trusted Real Estate Partner for Dholera Investment Opportunities
        </motion.p>
      </motion.div>

      <motion.div
        className="relative z-10 container-luxe pb-10"
        style={{ perspective: 900 }}
        initial={{ opacity: 0, y: 60, rotateX: 14 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ delay: 1.05, duration: 1.3, ease: EASE }}
      >
        <div className="glass-dark rounded-sm grid grid-cols-2 lg:grid-cols-4" data-testid="hero-stat-bar">
          {HERO_STATS.map((s, i) => (
            <div key={s.label} className={`px-6 py-5 ${i % 2 === 1 ? "border-l" : ""} ${i > 1 ? "border-t lg:border-t-0" : ""} ${i > 0 ? "lg:border-l" : ""} border-white/10`}>
              <p className="font-mono text-xl sm:text-2xl text-gold font-semibold" data-testid={`hero-stat-value-${i}`}>{s.value}</p>
              <p className="mt-1 text-[11px] sm:text-xs text-white/65 leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          Figures as reported by DSIRDA / PIB & press sources (2026)
        </p>
      </motion.div>

      <motion.a
        href="#why-dholera"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-gold transition-colors duration-300 hidden lg:block"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        aria-label="Scroll to explore"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.a>
    </section>
  );
}
